import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import { api } from '@/lib/api';
import type { TemplateCalendarResponse, CalendarFilter, BulkUpdateCalendarDay, DaysPattern, CalendarDay, ShiftPattern, CustomPattern } from '@/types/schedule.types';

// Generate calendar from weekly pattern (DaysPattern)
const generateCalendarFromWeeklyPattern = (
  daysPattern: DaysPattern,
  year: number,
  month: number
): CalendarDay[] => {
  const calendar: CalendarDay[] = [];
  const daysInMonth = new Date(year, month, 0).getDate();
  const dayNames = ['sunday', 'monday', 'tuesday', 'wednesday', 'thursday', 'friday', 'saturday'] as const;

  for (let day = 1; day <= daysInMonth; day++) {
    const date = new Date(year, month - 1, day);
    const dayOfWeek = dayNames[date.getDay()];
    const daySchedule = daysPattern[dayOfWeek as keyof DaysPattern];

    // Determine if working day - matches backend logic
    const isWorking = daySchedule?.isWorking ?? false;

    calendar.push({
      id: `generated-${year}-${month}-${day}`,
      template_id: '',
      date: `${year}-${String(month).padStart(2, '0')}-${String(day).padStart(2, '0')}`,
      shift_start: isWorking && daySchedule?.start ? daySchedule.start : null,
      shift_end: isWorking && daySchedule?.end ? daySchedule.end : null,
      is_working_day: isWorking,
      schedule_type: isWorking ? 'working' : 'day_off',
    });
  }

  return calendar;
};

// Generate calendar from shift pattern (ShiftPattern)
const generateCalendarFromShiftPattern = (
  shiftPattern: ShiftPattern,
  year: number,
  month: number
): CalendarDay[] => {
  const calendar: CalendarDay[] = [];
  const daysInMonth = new Date(year, month, 0).getDate();
  const cycleLength = shiftPattern.work_days + shiftPattern.off_days;

  // Calculate days since cycle start
  const cycleStartDate = new Date(shiftPattern.cycle_start_date);
  const monthStart = new Date(year, month - 1, 1);

  for (let day = 1; day <= daysInMonth; day++) {
    const currentDate = new Date(year, month - 1, day);
    const daysSinceCycleStart = Math.floor(
      (currentDate.getTime() - cycleStartDate.getTime()) / (1000 * 60 * 60 * 24)
    );

    // Handle negative days (before cycle start)
    const positionInCycle = ((daysSinceCycleStart % cycleLength) + cycleLength) % cycleLength;
    const isWorking = positionInCycle < shiftPattern.work_days;

    calendar.push({
      id: `generated-${year}-${month}-${day}`,
      template_id: '',
      date: `${year}-${String(month).padStart(2, '0')}-${String(day).padStart(2, '0')}`,
      shift_start: isWorking ? shiftPattern.work_time.start : null,
      shift_end: isWorking ? shiftPattern.work_time.end : null,
      is_working_day: isWorking,
      schedule_type: isWorking ? 'working' : 'day_off',
    });
  }

  return calendar;
};

// Generate calendar from custom pattern (CustomPattern)
const generateCalendarFromCustomPattern = (
  customPattern: CustomPattern,
  year: number,
  month: number
): CalendarDay[] => {
  const calendar: CalendarDay[] = [];
  const daysInMonth = new Date(year, month, 0).getDate();
  const cycleLength = customPattern.cycle_length;

  // Use cycle_start_date from pattern or default to first day of year
  const cycleStart = customPattern.cycle_start_date
    ? new Date(customPattern.cycle_start_date)
    : new Date(year, 0, 1);

  for (let day = 1; day <= daysInMonth; day++) {
    const currentDate = new Date(year, month - 1, day);
    const daysSinceCycleStart = Math.floor(
      (currentDate.getTime() - cycleStart.getTime()) / (1000 * 60 * 60 * 24)
    );

    // Handle negative days (before cycle start)
    const positionInCycle = ((daysSinceCycleStart % cycleLength) + cycleLength) % cycleLength;
    const dayPattern = customPattern.pattern.find((p: { day: number; isWorking: boolean; start?: string; end?: string }) => p.day === positionInCycle + 1);

    const isWorking = dayPattern?.isWorking ?? false;

    calendar.push({
      id: `generated-${year}-${month}-${day}`,
      template_id: '',
      date: `${year}-${String(month).padStart(2, '0')}-${String(day).padStart(2, '0')}`,
      shift_start: isWorking && dayPattern?.start ? dayPattern.start : null,
      shift_end: isWorking && dayPattern?.end ? dayPattern.end : null,
      is_working_day: isWorking,
      schedule_type: isWorking ? 'working' : 'day_off',
    });
  }

  return calendar;
};

// Type guards for pattern detection by structure
const isShiftPattern = (pattern: unknown): pattern is ShiftPattern => {
  if (!pattern || typeof pattern !== 'object') return false;
  const p = pattern as Record<string, unknown>;
  return p.type === 'shift' && typeof p.work_days === 'number' && typeof p.off_days === 'number';
};

const isCustomPattern = (pattern: unknown): pattern is CustomPattern => {
  if (!pattern || typeof pattern !== 'object') return false;
  const p = pattern as Record<string, unknown>;
  return p.type === 'custom' && Array.isArray(p.pattern) && typeof p.cycle_length === 'number';
};

const isDaysPattern = (pattern: unknown): pattern is DaysPattern => {
  if (!pattern || typeof pattern !== 'object') return false;
  const p = pattern as Record<string, unknown>;
  // Check for any day of week property
  return 'monday' in p || 'tuesday' in p || 'wednesday' in p ||
         'thursday' in p || 'friday' in p || 'saturday' in p || 'sunday' in p;
};

// Main function to generate calendar based on pattern type
const generateCalendarFromPattern = (
  pattern: DaysPattern | ShiftPattern | CustomPattern,
  year: number,
  month: number,
  _recurrenceType?: string // Keep parameter for compatibility but detect by structure
): CalendarDay[] => {
  // Detect pattern type BY STRUCTURE, not by recurrenceType
  // This handles cases where 1C sends weekly pattern with recurrence_type="custom"

  if (isShiftPattern(pattern)) {
    return generateCalendarFromShiftPattern(pattern, year, month);
  }

  if (isCustomPattern(pattern)) {
    return generateCalendarFromCustomPattern(pattern, year, month);
  }

  // Default to weekly pattern if it has day properties
  if (isDaysPattern(pattern)) {
    return generateCalendarFromWeeklyPattern(pattern, year, month);
  }

  // Fallback: empty calendar
  console.warn('Unknown pattern type, returning empty calendar:', pattern);
  return [];
};

export const useTemplateCalendar = (
  templateId: string | null,
  year: number,
  month: number
) => {
  return useQuery<TemplateCalendarResponse>({
    queryKey: ['templateCalendar', templateId, year, month],
    queryFn: async () => {
      if (!templateId) throw new Error('Template ID is required');

      try {
        // api.get() возвращает данные напрямую, без обёртки response.data
        const data = await api.get(
          `/api/v1/schedule-templates/${templateId}/calendar`,
          {
            params: { year, month }
          }
        );

        if (!data.calendar || data.calendar.length === 0) {
          if (data.days_pattern) {
            return {
              template_id: templateId,
              calendar: generateCalendarFromPattern(
                data.days_pattern,
                year,
                month
              ),
              is_generated: true,
              template_name: data.template_name,
              year,
              month
            };
          }
        }

        return data;
      } catch (error) {
        console.error('Error fetching calendar:', error);
        throw error;
      }
    },
    enabled: !!templateId,
    staleTime: 10 * 60 * 1000,
    gcTime: 30 * 60 * 1000,
  });
};

export const useTemplateCalendarByRange = (
  templateId: string | null,
  dateFrom?: string,
  dateTo?: string
) => {
  return useQuery<TemplateCalendarResponse>({
    queryKey: ['templateCalendarRange', templateId, dateFrom, dateTo],
    queryFn: async () => {
      if (!templateId) throw new Error('Template ID is required');

      // api.get() возвращает данные напрямую
      const data = await api.get(
        `/api/v1/schedule-templates/${templateId}/calendar`,
        {
          params: { dateFrom, dateTo }
        }
      );

      return data;
    },
    enabled: !!templateId && !!dateFrom && !!dateTo,
  });
};

export const useBulkUpdateCalendar = () => {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: async ({
      templateId,
      updates
    }: {
      templateId: string;
      updates: BulkUpdateCalendarDay[]
    }) => {
      // api.patch() возвращает данные напрямую
      const data = await api.patch(
        `/api/v1/schedule-templates/${templateId}/calendar/bulk`,
        { updates }
      );
      return data;
    },
    onSuccess: (_, variables) => {
      queryClient.invalidateQueries({
        queryKey: ['templateCalendar', variables.templateId]
      });
      queryClient.invalidateQueries({
        queryKey: ['templateCalendarRange', variables.templateId]
      });
    },
  });
};

export const useUpdateCalendarDays = (templateId: string | null) => {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: async ({
      dayIds,
      updates
    }: {
      dayIds: string[];
      updates: Partial<CalendarDay>;
    }) => {
      if (!templateId) throw new Error('Template ID is required');

      const bulkUpdates: BulkUpdateCalendarDay[] = dayIds.map(dayId => ({
        day_id: dayId,
        ...updates
      }));

      // api.patch() возвращает данные напрямую
      const data = await api.patch(
        `/api/v1/schedule-templates/${templateId}/calendar/bulk`,
        { updates: bulkUpdates }
      );
      return data;
    },
    onSuccess: () => {
      if (templateId) {
        queryClient.invalidateQueries({
          queryKey: ['templateCalendar', templateId]
        });
      }
    },
  });
};

export const useSyncTemplateCalendar = () => {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: async (templateId: string) => {
      // api.post() возвращает данные напрямую
      const data = await api.post(
        `/api/v1/1c/template-calendars/sync`,
        { template_id: templateId }
      );
      return data;
    },
    onSuccess: (_, templateId) => {
      queryClient.invalidateQueries({
        queryKey: ['templateCalendar', templateId]
      });
    },
  });
};